import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import LogoHB4 from '../../assets/images/hb4_reduzida_colorida.png';

const styles = StyleSheet.create({
  // ===== Página inteira =====
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 12,
    lineHeight: 1.4,
  },

  // ===== Cabeçalho principal =====
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  logo: {
    width: 50,
    height: 50,
    objectFit: 'contain',
  },

  // ===== Seção “Dados do paciente” =====
  personalDataContainer: {
    marginBottom: 16,
  },
  personalDataTitleContainer: {
    backgroundColor: '#e0e0e0',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 2,
    marginBottom: 8,
  },
  personalDataTitle: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  personalDataRow: {
    marginBottom: 4,
  },
  personalDataLabel: {
    fontWeight: 'bold',
  },

  // ===== Seção de cada relatório clínico =====
  section: {
    marginBottom: 24,
  },
  subsectionContainer: {
    backgroundColor: '#e0e0e0',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 2,
    marginBottom: 12,
  },
  subsectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
  },

  // ===== Texto normal =====
  paragraphContainer: {
    marginBottom: 8,
  },
  text: {
    marginBottom: 0,
  },
  bold: {
    fontWeight: 'bold',
  },
  bullet: {
    marginLeft: 10,
    marginBottom: 6,
  },

  // ===== Caso não haja relatórios =====
  emptyState: {
    fontStyle: 'italic',
    color: '#666',
  },
});

const ReportSection = ({ r }) => (
  <View style={styles.section}>
    <View style={styles.subsectionContainer}>
      <Text style={styles.subsectionTitle}>
        {r.disorder} {r.scale}
      </Text>
    </View>

    {r.text
      .split('\n\n')
      .map((paragraph, pIndex) => {
        const lines = paragraph.split('\n');
        return (
          <View key={pIndex} style={styles.paragraphContainer}>
            {lines.map((line, lIndex) => {
              const trimmed = line.trim();
              if (trimmed === '') return null;

              if (trimmed.startsWith('- ')) {
                const content = trimmed.substring(2);
                return (
                  <Text key={lIndex} style={styles.bullet}>
                    {'\u2022 '}
                    {content}
                  </Text>
                );
              }

              return (
                <Text key={lIndex} style={styles.text}>
                  {line
                    .split(/(\*\*[^*]+\*\*)/g)
                    .map((seg, segIdx) => {
                      const match = seg.match(/^\*\*(.+)\*\*$/);
                      if (match) {
                        return (
                          <Text key={segIdx} style={styles.bold}>
                            {match[1]}
                          </Text>
                        );
                      }
                      return seg;
                    })}
                </Text>
              );
            })}
          </View>
        );
      })}
  </View>
);

export default function PatientDashboardPdf({
  report,
  patientName,
  patientSexo,
  patientCPF,
}) {
  if (!report || !Array.isArray(report.reports)) {
    return (
      <Document>
        <Page style={styles.page}>
          <Text style={styles.emptyState}>Nenhum relatório disponível.</Text>
        </Page>
      </Document>
    );
  }

  const allReports = report.reports;

  return (
    <Document>
      <Page style={styles.page}>
        {/* ===== CABEÇALHO PRINCIPAL ===== */}
        <View style={styles.header}>
          <Text style={styles.title}>Relatório clínico</Text>
          <Image src={LogoHB4} style={styles.logo} />
        </View>

        {/* ===== SEÇÃO “DADOS DO PACIENTE” ===== */}
        <View style={styles.personalDataContainer}>
          <View style={styles.personalDataTitleContainer}>
            <Text style={styles.personalDataTitle}>Dados pessoais</Text>
          </View>

          {/* Nome */}
          <Text style={styles.personalDataRow}>
            <Text style={styles.personalDataLabel}>Nome: </Text>
            {patientName || '—'}
          </Text>

          {/* Sexo (se fornecido) */}
          {patientSexo && (
            <Text style={styles.personalDataRow}>
              <Text style={styles.personalDataLabel}>Sexo: </Text>
              {patientSexo}
            </Text>
          )}

          {/* CPF (se fornecido) */}
          {patientCPF && (
            <Text style={styles.personalDataRow}>
              <Text style={styles.personalDataLabel}>CPF: </Text>
              {patientCPF}
            </Text>
          )}
        </View>

        {/* ===== SEÇÃO DE TODOS OS RELATÓRIOS ===== */}
        {allReports.map((r, idx) => (
          <ReportSection key={idx} r={r} />
        ))}
      </Page>
    </Document>
  );
}
